import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { CheckCircle2, Circle, ArrowRight, X, Rocket } from 'lucide-react';

// Reusable dashboard "Getting Started" checklist + profile completion widget.
export const GettingStarted = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/onboarding/progress`);
      setData(res.data);
      if (res.data.dismissed) setHidden(true);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (item) => {
    setBusy(item.id);
    try {
      await axios.post(`${API_URL}/onboarding/checklist/toggle`, { item_id: item.id, done: !item.done });
      await load();
    } catch (e) { /* ignore */ }
    setBusy(null);
  };

  const dismiss = async () => {
    setHidden(true);
    try { await axios.post(`${API_URL}/onboarding/dismiss`, { dismissed: true }); } catch (e) { /* ignore */ }
  };

  if (!data || hidden) return null;

  const doneCount = data.checklist.filter((c) => c.done).length;
  const allDone = doneCount === data.checklist.length && data.checklist.length > 0;
  const pct = data.profile_completion || 0;

  return (
    <div className="bg-white rounded-2xl border border-secondary/30 shadow-sm p-5 mb-6" data-testid="getting-started-widget">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-secondary/15 flex items-center justify-center flex-shrink-0">
            <Rocket className="w-5 h-5 text-secondary" />
          </div>
          <div>
            <h3 className="font-serif text-lg font-bold text-primary">Getting Started</h3>
            <p className="text-xs text-gray-500">{allDone ? "You're all set — nice work!" : `${doneCount} of ${data.checklist.length} steps complete`}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Profile completion ring */}
          <div className="relative w-14 h-14 flex-shrink-0" data-testid="profile-completion-ring" title="Profile completion">
            <svg className="w-14 h-14 -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#eee" strokeWidth="3" />
              <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#C5A059" strokeWidth="3" strokeDasharray={`${pct} ${100 - pct}`} strokeLinecap="round" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-primary">{pct}%</span>
          </div>
          <button onClick={dismiss} className="text-gray-300 hover:text-gray-500" aria-label="dismiss" data-testid="getting-started-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <ul className="space-y-2">
        {data.checklist.map((item) => (
          <li key={item.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors" data-testid={`gs-item-${item.id}`}>
            <button onClick={() => toggle(item)} disabled={busy === item.id} className="flex-shrink-0" aria-label="toggle" data-testid={`gs-toggle-${item.id}`}>
              {item.done ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-gray-300" />}
            </button>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold truncate ${item.done ? 'line-through text-gray-400' : 'text-gray-800'}`}>{item.label}</p>
            </div>
            {!item.done && (
              <Link to={item.action_url} className="text-xs font-semibold text-primary hover:underline whitespace-nowrap flex items-center gap-0.5">
                {item.action_label} <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default GettingStarted;
